<template>
  <div ref="mapContainerElement" class="map-container" :class="{'fullscreen': map.fullscreen}">
    <l-map @ready="handleMapReady"
           @update:bounds="handleMapUpdateBounds">

      <l-tile-layer-custom :url.sync="map.mode.value" />

      <!-- fullscreen switcher -->
      <l-control v-if="fullscreenSwitch" position="topleft">
        <div class="fullscreen-switcher">
          <i class="fa fa-arrows-alt" @click="toggleFullscreen"></i>
        </div>
      </l-control>

      <!-- map mode -->
      <l-control v-if="mapModeSwitch" position="bottomright">
        <b-form-group class="mb-0">
          <b-form-select
            class="map-overlay"
            v-model="map.mode.value"
            :options="map.mode.choices" />
        </b-form-group>
      </l-control>

      <!-- controls -->
      <l-control position="topleft">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-top-left"></slot>
      </l-control>

      <l-control position="topright">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-top-right"></slot>
      </l-control>

      <l-control position="bottomleft">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-bottom-left"></slot>
      </l-control>

      <l-control position="bottomright">
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <slot name="control-bottom-right"></slot>
      </l-control>

      <!-- vessels -->
      <l-marker
        v-for="vessel in vessels"
        :key="`vessel-${vessel.imo}`"
        :lat-lng="getVesselCoords(vessel)"
        :icon="getVesselDivIcon(vessel)"
        :options="{ vessel: vessel, riseOnHover: true}">

        <rows-map-popup
          title="Vessel details"
          @ready="handleVesselPopupReady"
          :item-id="vessel.imo"
          :rows="vesselPopupData">

          <slot name="popup-extras"
                slot="extras"
                v-bind:context="{itemId: vessel.imo}" >
            <!-- forwards "extras" slot as "popup-extras" -->
          </slot>

        </rows-map-popup>
      </l-marker>

      <!-- bubbles -->
      <template v-if="bubblesEnabled">
        <l-circle-marker
          v-for="(bubble, index) in bubblesFormatted"
          :key="`bubble-${index}`"
          :lat-lng="bubble.coords"
          :radius="bubble.radius"
          :color="bubble.color"
          :fillColor="bubble.fillColor">

          <rows-map-popup
            title="Details"
            :item-id="index"
            :rows="getBubblePopupData(bubble)" />

        </l-circle-marker>

        <!-- bubbles legend -->
        <l-control class="legend map-overlay" position="bottomleft">
          <p class="legend__item">
            <span class="circle circle-blue"></span>
            <span>Origin</span>
          </p>
          <p class="legend__item">
            <span class="circle circle-red"></span>
            <span>Destination</span>
          </p>
          <p v-if="maxBubbleVolume" class="legend__item">
            <span class="circle-size circle-grey"></span>
            <span>Largest bubble = {{ formatVolume(maxBubbleVolume) }}</span>
          </p>
        </l-control>
      </template>

      <!-- alerts -->
      <div class="alerts">
        <b-alert
          v-for="(alert, index) in alerts"
          :key="`alert-${index}`"
          :variant="alert.variant"
          :show="true"
          class="map-overlay"
          dismissible
          fade>
          <slot slot="dismiss">
            <div @click="$emit('alert-dismissed', alert)">&times;</div>
          </slot>
          {{ alert.text }}
        </b-alert>
      </div>


      <!-- optional map components -->
      <slot name="map-extras" v-bind:context="{ map }">
        <!--
          put additional leaflet components here
          <template #map-extras> ... </template>
        -->
      </slot>

    </l-map>
  </div>
</template>

<script>
import getNested from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';

import L from 'leaflet';
import {
  LMap,
  LControl,
  LMarker,
  LCircleMarker,
} from 'vue2-leaflet';

import { leafletBoundsToApi } from '@/shared/coordinate-utils';
import { mapModes } from '@/shared/constants/maps';
import LTileLayerCustom from '@/base_components/map/LTileLayerCustom';
import { formatVolume } from '@/shared/number-utils';
import RowsMapPopup from '@/base_components/map/RowsMapPopup';
import { getFormattedVesselData } from '@/shared/api_formatters/vessels';

export default {
  name: 'base-map',

  components: {
    RowsMapPopup,
    LMap,
    LTileLayerCustom,
    LControl,
    LMarker,
    LCircleMarker,
  },

  props: {
    fullscreenSwitch: {
      type: Boolean,
      default: true,
    },

    mapModeSwitch: {
      type: Boolean,
      default: true,
    },

    mapCenter: {
      // this parameter is used to .setZoom() the map when it's needed
      // must be an array or null/undefined
      type: Array,
      default: () => [47.41322, -1.219482],
    },

    mapZoom: {
      type: Number,
      default: 2,
    },

    mapBounds: {
      // this parameter is used to .fitBounds() the map when it's needed
      type: Array,
      default: null,
    },

    vessels: {
      type: Array,
      default: () => [],
    },

    selectedVesselImo: {
      type: Number,
      default: null,
    },

    highlightedVesselImo: {
      type: Number,
      default: null,
    },

    selectedVesselDetails: {
      type: Object,
      default: () => ({}),
    },

    originBubbles: {
      type: Array,
      default: () => [],
    },

    destinationBubbles: {
      type: Array,
      default: () => [],
    },

    bubbleRadiusMultiplier: {
      type: Number,
      default: 300,  // here it's possible to adjust bubble sizes
    },

    alerts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      map: {
        zoom: this.mapZoom,
        center: this.mapCenter,
        resizeObserver: null,
        fullscreen: false,
        instance: null,
        mode: {
          value: '',
          choices: mapModes,
        },
      }
    };
  },

  mounted() {
    this.$refs.mapContainerElement.addEventListener('keydown', this.keydownHandler);
  },

  beforeDestroy() {
    this.$refs.mapContainerElement.removeEventListener('keydown', this.keydownHandler);

    if(this.map.resizeObserver) {
      this.map.resizeObserver.unobserve(this.$refs.mapContainerElement);
    }
  },

  methods: {
    formatVolume: formatVolume,

    keydownHandler(evt) {
      if (evt.key === 'Escape' && this.map.fullscreen) {
        // exit from fullscreen mode
        this.toggleFullscreen();
      }
    },

    async handleMapReady(mapObject) {
      // track map container dimesions and redraw a map if required
      this.resizeObserver = new ResizeObserver(this.handleMapContainerResized)
      this.resizeObserver.observe(this.$refs.mapContainerElement);

      this.map.instance = mapObject;
      if(this.mapBounds) {
        this.setMapBounds(this.mapBounds);
      } else {
        this.setMapCenterAndZoom();
      }
      await this.handleMapUpdateBounds(this.map.instance.getBounds());
    },

    handleMapContainerResized() {
      if(getNested(this, 'map.instance._mapPane')) {
        // prevents redraw errors in dev mode (when a component is live-updated in browser)
        this.map.instance.invalidateSize();
      }
    },

    async handleMapUpdateBounds(data) {
      this.$emit('map-update', leafletBoundsToApi(data));
    },

    handleVesselPopupReady(evt) {
      const vesselIMO = getNested(evt, 'options.itemId', null);
      if(vesselIMO && this.selectedVesselImo === vesselIMO) {
        evt._source.openPopup();
      }

      this.$emit('vessel-popup-ready', evt);
    },

    getVesselCoords(vessel) {
      // console.log('vessel', JSON.stringify(vessel, null, 2));
      const coordinates = getNested(vessel, 'geometry.coordinates', [0.0, 0.0]);
      return [
        coordinates[1],
        coordinates[0],
      ];
    },

    getVesselDivIcon(vessel) {
      let iconOptions = this.getVesselIconOptions(vessel);

      return L.divIcon({
        html: this.getVesselIconHTMLElement(vessel, iconOptions),
        iconSize: [25, 25],
        iconAnchor: [10, 10],
        popupAnchor: [-5, -5],
        className: `vessel-${iconOptions.ladenStatus}`,
      });
    },

    getVesselIconHTMLElement(vessel, iconOptions) {
      // canvas is created in "@ready" on a marker element (cause we don't know actual dimensions)
      let {ladenStatus, slowStatus} = iconOptions;
      const course = getNested(vessel, 'position.course', 0);

      let element = document.createElement('div');
      element.className = `vessel-icon vessel-icon-${ladenStatus} vessel-icon-${slowStatus}`;
      element.style.transform = `rotate(${course}deg)`;
      element.style.backgroundImage = (
        'url(' + require(`@/assets/icons/vessels/vessel-${ladenStatus}-${slowStatus}.svg`) + ')'
      );
      return element;
    },

    getVesselIconOptions(vessel) {
      let ladenStatus = 'error';
      if (vessel.state && vessel.state.ladenStatus) {
        ladenStatus = vessel.state.ladenStatus === 'Laden' ? 'dark' : 'light';
      }

      if (this.highlightedVesselImo === vessel.imo) {
        ladenStatus = 'highlight';
      }

      let slowStatus = 'circle'; // we don't know speed this case, let's set as a circle
      if (vessel.position && typeof vessel.position.speed === 'number') {
        slowStatus = vessel.position.speed < 1 ? 'circle' : 'arrow';
      }

      return {ladenStatus, slowStatus};
    },

    getVesselPopupData(vesselIMO) {
      // basic popupData retrieved from this.vessels list
      // additional data for a selected vessel comes from props
      const vessel = this.vessels.find(item => item.imo === vesselIMO);
      return getNested(vessel, 'popupData', {})
    },

    getBubblePopupData(bubble) {
      return getNested(bubble, 'popupData', []);
    },

    openVesselPopup(vesselIMO) {
      if(!this.map.instance) {
        return;
      }

      const markerLayer = Object.values(this.map.instance._layers).find(layer => {
        const isMarkerLayer = getNested(layer, 'options.pane') === 'markerPane';
        const isTargetMarker = getNested(layer, 'options.vessel.imo') === vesselIMO;
        return isMarkerLayer && isTargetMarker;
      });

      if(markerLayer) {
        markerLayer.openPopup();
      }
    },

    toggleFullscreen() {
      // todo: fitBounds to the current bounds when fullscreen is toggled (both directions)

      this.map.fullscreen = !this.map.fullscreen;

      this.$nextTick(() => {
        if (this.map.fullscreen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = 'auto';
        }
        this.map.instance.invalidateSize();
      });
    },

    setMapBounds(bounds) {
      if(bounds && this.map.instance) {
        this.map.instance.fitBounds(bounds, {maxZoom: 13, padding: [30, 30]});
      }
    },

    setMapCenterAndZoom() {
      if(this.map.instance) {
        this.map.instance.setView(this.mapCenter, this.mapZoom);
      }
    },
  },

  computed: {
    vesselPopupDataBasic() {
      if(this.selectedVesselImo) {
        return this.getVesselPopupData(this.selectedVesselImo);
      } else {
        return {'Empty data': '--'};
      }
    },

    vesselPopupData() {
      if(isEmpty(this.selectedVesselDetails)) {
        return this.vesselPopupDataBasic;
      } else {
        if(this.selectedVesselDetails.imo === this.selectedVesselImo) {
          return getFormattedVesselData(this.selectedVesselDetails);
        } else {
          return this.vesselPopupDataBasic;
        }
      }
    },

    bubblesEnabled() {
      return !isEmpty(this.originBubbles) || !isEmpty(this.destinationBubbles);
    },

    bubblesFormatted() {
      const origins = this.originBubbles.map(item => ({
        ...item,
        color: '#3388ff',
        fillColor: '#3388ff',
      }));

      const destinations = this.destinationBubbles.map(item => ({
        ...item,
        color: '#ff6361',
        fillColor: '#ff6361',
      }));

      return origins.concat(destinations).map(item => ({
        ...item,
        radius: Math.sqrt(this.bubbleRadiusMultiplier * item.volume / this.maxBubbleVolume),
      }));
    },

    maxBubbleVolume() {
      let volumes = this.originBubbles.map(item => item.volume);
      volumes = volumes.concat(this.destinationBubbles.map(item => item.volume));

      return isEmpty(volumes) ? 1 : Math.max(...volumes);
    },
  },

  watch: {
    mapBounds(bounds) {
      if(bounds && this.map.instance) {
        this.map.instance.fitBounds(
          bounds,
          { maxZoom: 13, padding: [30, 30] }
        );
      }
    },

    mapZoom() {
      this.setMapCenterAndZoom();
    },

    mapCenter() {
      this.setMapCenterAndZoom();
    },

    selectedVesselImo(vesselIMO) {
      this.openVesselPopup(vesselIMO);
    },
  },
}
</script>


<style scoped>
  .map-container >>> .vessel-highlight {
    z-index: 2000 !important;
  }

  .map-container >>> .vessel-icon {
    z-index: 3000 !important;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }

  .map-container >>> .vessel-icon-arrow {
    width: 24px;
    height: 24px;
  }

  .map-container >>>  .vessel-icon-circle {
    width: 10px;
    height: 10px;
  }

  .map-container >>>  .leaflet-tooltip {
    font-size: 14px;
    font-weight: 800;
  }

  .map-container >>> .alert-dismissible .close {
    padding: 0.75rem 0.8rem;
  }

  .map-container >>> .leaflet-popup-content {
    min-width: 200px;
    min-height: 100px;
  }
</style>


<style scoped lang="scss">
  .map-container {
     z-index: 10;
    height: 100%;
    position: relative;

    // fullscreen toggle button
    &.fullscreen {
      position: fixed;
      height: 100vh;
      width: 100vw;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      z-index: 1050;
    }

    .vessel-count {
      padding: 0.25rem 0.5rem;
      border-radius: 3px;
    }

    // bubbles legend
    .legend {
      margin: 10px;
      padding: 5px 10px;
      border-radius: 5px;

      &__item {
        margin: 0;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }
      &__item:not(:last-child) {
        margin-bottom: 5px;
      }

      .circle {
        height: 12px;
        width: 12px;
        margin-right: 10px;
        border-radius: 50%;
      }

      .circle-size {
        height: 15px;
        width: 15px;
        margin-right: 10px;
        border-radius: 50%;
      }

      .circle-red {
        border: 3px rgb(255, 99, 97) solid;
        background-color: rgba(255, 99, 97, 0.2);
      }
      .circle-blue {
        border: 3px rgb(51, 136, 255) solid;
        background-color: rgba(51, 136, 255, 0.2);
      }
      .circle-grey {
        border: 3px rgb(177, 177, 177) solid;
        background-color: rgba(182, 184, 187, 0.2);
      }
    }

    .alerts {
      position: absolute;
      bottom: 5px;
      left: 220px;
      z-index: 500;
      min-width: 200px;
      max-width: calc(100% - 380px);

      .alert {
        margin-bottom: 5px;
      }
    }
  }
</style>
